import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  SummonOption2,
  CharacterSpell,
  Attack,
  ExtraEffectsFeature,
  CharacterFeature,
  ExtraEffectsOfTypeFeature,
  CharacterAction,
  CharacterExtraEffects,
  StoredSpell,
  CharacterSheetInterface,
} from "@dndModels";
import { DamageType, SpellLevel } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import DataUtilities from '../../../utilities/common_utilities';

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

import CharacterSummonOptions from "@dndComponents/character/misc/CharacterSummonOptions";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import LoadingBox from "@components/input/LoadingBox";
import DisplaySummonOption from "@dndComponents/display/DisplaySummonOption";

interface Props {
  char_int: CharacterSheetInterface;
  char?: Character;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  action: CharacterAction;
  effect: AbilityEffect;
  cee?: CharacterExtraEffects;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  ee_cf?: CharacterFeature;
  type_cf?: CharacterFeature;
  shared: boolean;
  level: SpellLevel;
  wide: boolean;
  show_extra_effects?: boolean;
  attack_mod?: number;
  main_damage_type?: DamageType;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void; // For slots, resources, and concentration
  onCostChange: (costs: { [resource: string]: number }) => void;
  hide_touch?: boolean;
  extra_string: string;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function CharacterSummonEffect({
  char_int,
  char: char2,
  spell,
  action,
  effect,
  cee,
  processing,
  disabled,
  ee_cf,
  shared,
  level,
  attack_mod = 0,
  main_damage_type,
  hide_touch = false,
  onChange,
  onCostChange,
  extra_string,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  // const [ping, setPing] = useState(false);
  // const [keep_rolls, setKeepRolls] = useState<any[]>([]);
  const [summon_option_list, setSummonOptionList] = useState<SummonOption2[]>([]);
  const [selected_option, setSelectedOption] = useState<SummonOption2 | null>(null);
  // const [selected_id, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const char = char2 ? char2 : char_int instanceof Character ? char_int : undefined;

  if (!char) {
    return <></>;
  }

  const load = useCallback( async () => {
    // if (ee_cf && ee_cf.name.includes("Blood")) {
    //   debugger;
    // }
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({ slot_level: level, char, item_id, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
      spell ? char_util.getPotence({ slot_level: level, char, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
    // let extra_string = "";
    if (potence) {
      // if (spell && spell.name.includes("Familiar")) {
      //   debugger;
      // }
      setLoading(true);
      // extra_string = potence.extra;
      const options = await char_util.getSummonOption2s(char, potence); // , action.name.includes("Pact")
      setSummonOptionList(options);
      setLoading(false);
    }
  }, []);
   
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id)));
  const item_id = spell instanceof CharacterAbility ? spell.item_id : (spell instanceof CharacterSpell ? spell.source_true_id : "");

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          onClose={() => { 
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}
        >
          <>
            { open && renderSummonOptions() }
          </>
        </ModalOrDrawer>
      </>
    );
  }

  const renderSummonOptions = () => {
    const option = summon_option_list.length === 1 ? summon_option_list[0] : selected_option;
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} className="header centered">
          Summon Options
        </Grid>
        <Grid size={12}>
          <CharacterSummonOptions 
            name="Choose"
            character={char}
            value={option}
            summon_option_list={summon_option_list}
            shared={shared}
            index
            onChange={setSelectedOption}
          />
        </Grid>
        { selected_option && char instanceof Character &&
          <>
            { spell ?
              <Grid size={12}>
                <DisplaySummonOption
                  char={char}
                  obj={selected_option}
                  spell={spell}
                  level={level}
                />
              </Grid>
            : ee_cf &&
              <Grid size={12}>
                <DisplaySummonOption
                  char={char}
                  obj={selected_option}
                  spell={ee_cf}
                  level={level}
                />
              </Grid>
            }
          </>
        }
      </Grid>
    );
  }

  const summonSelected = async (selected_option: SummonOption2) => {
    // debugger;
    if (char instanceof Character) {
      if (spell) {
        setProcessing(true);
        await char_util.summonMinions({ char, spell, level, selected_option });
        setProcessing(false);
        onChange(["Minions"]);
      } else if (ee_cf) {
        setProcessing(true);
        await char_util.summonMinions({ char, spell: ee_cf, level, selected_option });
        setProcessing(false);
        onChange(["Minions"]);
      }
    }
  }

  const viewSelected = () => {
    // Opens a modal with a viewable version of the statblock without actually summoning it
    setOpen(true);
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const option = summon_option_list.length === 1 ? summon_option_list[0] : selected_option;
    let range_string = spell instanceof Attack ? "Self" : char_util.getRangeString({ char, spell, effect });
    if (hide_touch && (range_string === "Touch" || range_string === "Self")) {
      range_string = "";
    }
    return (
      <Grid className="centered"
        container spacing={0} direction="row">
        <Grid size={4} className="centered">
          { range_string }{ extra_string !== "" ? (range_string !== "" ? ` / ${extra_string}` : extra_string) : "" }
        </Grid>
        <Grid size={4}>
          { summon_option_list.length !== 1 ?
            <CharacterSummonOptions 
              name="Choose"
              character={char}
              // potence={potence}
              value={option}
              summon_option_list={summon_option_list}
              shared={shared}
              onChange={setSelectedOption}
            />
          : option &&
            <>{ option.name }</>
          }
        </Grid>
        <Grid size={4}>
          <Grid container spacing={0} direction="row">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="Summon"
                disabled={ !enabled || option === null || option === undefined || processing }
                onClick={() => {
                  // debugger;
                  if (option) {
                    summonSelected(option);
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="View"
                disabled={ !enabled }
                onClick={() => {
                  viewSelected();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        { renderExtras() }
      </Grid>
    );
  }
}
